import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import MarkdownIt from 'markdown-it';
import { getPostBySlug, blogPosts } from '../data/blog';
import PageSEO from '../components/PageSEO';

const md = new MarkdownIt({ html: false, linkify: true, typographer: true });

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  const htmlContent = useMemo(
    () => (post?.content ? md.render(post.content) : ''),
    [post]
  );

  if (!post) {
    return (
      <div className="py-32 text-center">
        <h2 className="text-2xl font-bold text-slate-900 mb-4">Article introuvable</h2>
        <Link to="/blog" className="text-orange-600 font-semibold hover:underline">← Retour au blog</Link>
      </div>
    );
  }

  const related = blogPosts.filter((p) => p.slug !== slug).slice(0, 3);

  return (
    <>
      <PageSEO
        title={post.title}
        path={`/blog/${post.slug}`}
        description={post.excerpt || post.title}
      />
      <section className="bg-slate-900 py-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-sm text-slate-400 mb-4 flex items-center gap-2 flex-wrap">
            <Link to="/" className="hover:text-orange-400">Accueil</Link>
            <span>/</span>
            <Link to="/blog" className="hover:text-orange-400">Blog</Link>
            <span>/</span>
            <span className="text-white line-clamp-1">{post.title}</span>
          </div>
          <span className="inline-block bg-orange-600 text-white text-xs font-semibold px-3 py-1 rounded-full mb-4">{post.category}</span>
          <h1 className="text-3xl lg:text-4xl font-extrabold text-white leading-tight mb-5">{post.title}</h1>
          <div className="flex items-center gap-4 text-slate-400 text-sm">
            <span>Par {post.author}</span>
            <span>·</span>
            <span>{post.date}</span>
            <span>·</span>
            <span>{post.readTime} de lecture</span>
          </div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <img src={post.image} alt={post.title} className="w-full h-80 object-cover rounded-2xl mb-10 shadow-md" />

        <div
          className="prose prose-slate prose-lg max-w-none
            prose-headings:font-bold prose-headings:text-slate-900
            prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
            prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
            prose-p:text-slate-600 prose-p:leading-relaxed prose-p:mb-5
            prose-strong:text-slate-800
            prose-ul:space-y-1 prose-li:text-slate-600
            prose-table:text-sm prose-table:w-full
            prose-th:bg-slate-100 prose-th:text-slate-700 prose-th:font-semibold prose-th:px-3 prose-th:py-2 prose-th:text-left
            prose-td:px-3 prose-td:py-2 prose-td:border-b prose-td:border-slate-100 prose-td:text-slate-600
            prose-a:text-orange-600 prose-a:no-underline hover:prose-a:underline"
          dangerouslySetInnerHTML={{ __html: htmlContent }}
        />

        <div className="bg-orange-50 border-l-4 border-orange-500 rounded-r-xl p-6 my-8">
          <p className="font-bold text-orange-800 mb-2">💡 Bon à savoir</p>
          <p className="text-orange-700 text-sm leading-relaxed">
            Apex Rénovation vous accompagne dans tous vos projets de rénovation dans le Berry. Garantie décennale sur tous nos chantiers, devis gratuit et sans engagement.
          </p>
        </div>

        <div className="border-t border-slate-200 pt-8 mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <p className="text-sm text-slate-500 mb-1">Partagez cet article</p>
            <div className="flex gap-2">
              <button className="bg-blue-600 text-white text-xs font-semibold px-3 py-1.5 rounded-lg">Facebook</button>
              <button className="bg-sky-500 text-white text-xs font-semibold px-3 py-1.5 rounded-lg">Twitter</button>
              <button className="bg-blue-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg">LinkedIn</button>
            </div>
          </div>
          <Link to="/devis" className="bg-orange-600 hover:bg-orange-700 text-white font-bold px-6 py-3 rounded-lg transition-colors text-sm">
            Demander un devis gratuit →
          </Link>
        </div>
      </div>

      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-8">Articles connexes</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="bg-white rounded-xl overflow-hidden border border-slate-200 hover:shadow-md transition-shadow group">
                <img src={p.image} alt={p.title} className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="p-5">
                  <span className="inline-block bg-orange-100 text-orange-700 text-xs font-semibold px-2.5 py-1 rounded-full mb-2">{p.category}</span>
                  <h3 className="font-bold text-slate-900 text-sm leading-snug group-hover:text-orange-600 transition-colors">{p.title}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
